/**
 * Bank True-Up Module
 * Compares bank CSV exports against logged expenses to surface missed transactions.
 * All bank data is processed client-side only — nothing is stored in Firestore.
 */

import { db } from './firebase.js';
import {
  collection, doc, getDoc, updateDoc, query, where, orderBy, limit, getDocs, Timestamp
} from 'firebase/firestore';
import { getCurrentUser } from './auth.js';
import { getLinkingState } from './linkSharing.js';
import { getCurrentBudgetId } from './budget.js';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const trueUpState = {
  step: 1,
  rawCsvText: null,
  parsedTransactions: [],
  columnMapping: null,
  dateFrom: null,
  dateTo: null,
  loggedExpenses: [],
  unmatchedTransactions: [],
  matchedTransactions: [],
  addedFromTrueUp: new Set(),
  activeTab: 'unmatched',
  minimized: false
};

// ---------------------------------------------------------------------------
// CSV Parsing (quote-aware, mirrors budget.js parseCSV pattern)
// ---------------------------------------------------------------------------

function parseCSVText(text) {
  // Strip UTF-8 BOM
  text = text.replace(/^\uFEFF/, '');
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let inQuote = false;
    let cell = '';
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuote = !inQuote;
        }
      } else if (ch === ',' && !inQuote) {
        result.push(cell.trim());
        cell = '';
      } else {
        cell += ch;
      }
    }
    result.push(cell.trim());
    return result;
  };

  const firstRow = parseRow(lines[0]);

  // Detect headerless CSVs (e.g. Wells Fargo) — if the first cell looks like
  // a date value rather than a column label, treat all rows as data and
  // synthesise numeric column labels so auto-detection can still run.
  const looksLikeDate = /^\d{1,4}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(firstRow[0]?.trim());
  const headers = looksLikeDate
    ? firstRow.map((_, i) => `Column ${i + 1}`)
    : firstRow;
  const dataStartIndex = looksLikeDate ? 0 : 1;

  const rows = [];
  for (let i = dataStartIndex; i < lines.length; i++) {
    const row = parseRow(lines[i]);
    // Skip blank lines
    if (row.every(c => c === '')) continue;
    rows.push(row);
  }
  return { headers, rows, headerless: looksLikeDate };
}

// ---------------------------------------------------------------------------
// Column Auto-Detection
// ---------------------------------------------------------------------------

const DATE_PATTERNS = [
  /^transaction\s*date$/i,
  /^trans\.?\s*date$/i,
  /^date$/i,
  /^posted\s*date$/i,
];

const DESC_PATTERNS = [
  /^description$/i,
  /^memo$/i,
  /^merchant$/i,
  /^payee$/i,
  /^transaction\s*description$/i,
];

const AMOUNT_PATTERNS = [/^amount$/i, /^transaction\s*amount$/i];
const DEBIT_PATTERNS  = [/^debit$/i,  /^withdrawal$/i, /^debit\s*amount$/i];
const CREDIT_PATTERNS = [/^credit$/i, /^deposit$/i,    /^credit\s*amount$/i];

function matchPatterns(header, patterns) {
  return patterns.some(p => p.test(header.trim()));
}

function detectColumnMapping(headers, sampleRows) {
  let dateCol = -1, descCol = -1, amountCol = -1, debitCol = -1, creditCol = -1;

  headers.forEach((h, i) => {
    if (dateCol === -1   && matchPatterns(h, DATE_PATTERNS))   dateCol   = i;
    if (descCol === -1   && matchPatterns(h, DESC_PATTERNS))   descCol   = i;
    if (amountCol === -1 && matchPatterns(h, AMOUNT_PATTERNS)) amountCol = i;
    if (debitCol === -1  && matchPatterns(h, DEBIT_PATTERNS))  debitCol  = i;
    if (creditCol === -1 && matchPatterns(h, CREDIT_PATTERNS)) creditCol = i;
  });

  // Headerless CSV fallback (e.g. Wells Fargo): infer columns from data shape.
  // Scan sample rows to find the column that consistently looks like a date,
  // the column with the largest string values (description), and a numeric column.
  if (dateCol === -1 || descCol === -1) {
    if (sampleRows.length === 0) return null;
    const colCount = sampleRows[0].length;

    for (let i = 0; i < colCount && dateCol === -1; i++) {
      const vals = sampleRows.map(r => r[i]).filter(Boolean);
      if (vals.length > 0 && vals.every(v => /^\d{1,4}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(v.trim()))) {
        dateCol = i;
      }
    }
    for (let i = 0; i < colCount && descCol === -1; i++) {
      if (i === dateCol) continue;
      const vals = sampleRows.map(r => r[i]).filter(Boolean);
      // Description column: longest average string, not a pure number
      if (vals.length > 0 && vals.some(v => v.length > 10 && isNaN(parseFloat(v.replace(/[$,]/g, ''))))) {
        descCol = i;
      }
    }
    for (let i = 0; i < colCount && amountCol === -1; i++) {
      if (i === dateCol || i === descCol) continue;
      const vals = sampleRows.map(r => r[i]).filter(Boolean);
      if (vals.length > 0 && vals.every(v => !isNaN(parseFloat(v.replace(/[$,]/g, ''))))) {
        amountCol = i;
      }
    }

    if (dateCol === -1 || descCol === -1) return null;
  }

  // Determine date format from first sample that has a value
  let dateFormat = 'MM/DD/YYYY';
  for (const row of sampleRows) {
    const val = row[dateCol];
    if (!val) continue;
    if (/^\d{4}-\d{2}-\d{2}/.test(val)) { dateFormat = 'YYYY-MM-DD'; break; }
    if (/^\d{2}\/\d{2}\/\d{4}/.test(val)) { dateFormat = 'MM/DD/YYYY'; break; }
    if (/^\d{2}-\d{2}-\d{4}/.test(val))  { dateFormat = 'MM-DD-YYYY'; break; }
  }

  // Determine sign convention if using single amount column
  let amountSign = 'negative-is-debit';
  if (amountCol !== -1 && sampleRows.length > 0) {
    const samples = sampleRows
      .map(r => parseFloat(r[amountCol]?.replace(/[$,]/g, '') || ''))
      .filter(n => !isNaN(n));
    if (samples.length > 0 && samples.every(n => n >= 0)) {
      amountSign = 'positive-is-debit';
    }
  }

  return {
    dateCol,
    descriptionCol: descCol,
    amountCol: amountCol !== -1 ? amountCol : null,
    debitCol:  debitCol  !== -1 ? debitCol  : null,
    creditCol: creditCol !== -1 ? creditCol : null,
    dateFormat,
    amountSign: (debitCol !== -1 || creditCol !== -1) ? 'split-columns' : amountSign
  };
}

// ---------------------------------------------------------------------------
// Date Parsing
// ---------------------------------------------------------------------------

function parseDate(str, format) {
  if (!str) return null;
  // Remove time portion if present
  str = str.split(' ')[0].split('T')[0];
  let year, month, day;
  if (format === 'YYYY-MM-DD') {
    [year, month, day] = str.split('-').map(Number);
  } else if (format === 'MM/DD/YYYY') {
    [month, day, year] = str.split('/').map(Number);
  } else if (format === 'MM-DD-YYYY') {
    [month, day, year] = str.split('-').map(Number);
  } else {
    // Fallback: let JS parse it
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }
  if (!year || !month || !day) return null;
  const d = new Date(year, month - 1, day);
  return isNaN(d.getTime()) ? null : d;
}

// ---------------------------------------------------------------------------
// Transaction Building
// ---------------------------------------------------------------------------

function buildTransactions(headers, rows, mapping) {
  const transactions = [];
  rows.forEach((row, idx) => {
    const dateStr = row[mapping.dateCol];
    const description = row[mapping.descriptionCol] || '';
    const date = parseDate(dateStr, mapping.dateFormat);
    if (!date) return;

    let rawAmount = 0;
    let isCredit = false;

    if (mapping.amountSign === 'split-columns') {
      const debitStr  = mapping.debitCol  !== null ? row[mapping.debitCol]  : '';
      const creditStr = mapping.creditCol !== null ? row[mapping.creditCol] : '';
      const debitVal  = parseFloat((debitStr  || '').replace(/[$,]/g, '')) || 0;
      const creditVal = parseFloat((creditStr || '').replace(/[$,]/g, '')) || 0;
      if (debitVal > 0) {
        rawAmount = debitVal;
        isCredit  = false;
      } else if (creditVal > 0) {
        rawAmount = creditVal;
        isCredit  = true;
      } else {
        return; // skip zero rows
      }
    } else if (mapping.amountCol !== null) {
      rawAmount = parseFloat((row[mapping.amountCol] || '').replace(/[$,]/g, ''));
      if (isNaN(rawAmount) || rawAmount === 0) return;
      if (mapping.amountSign === 'negative-is-debit') {
        isCredit  = rawAmount > 0;
        rawAmount = Math.abs(rawAmount);
      } else {
        // positive-is-debit (USAA style)
        isCredit  = rawAmount < 0;
        rawAmount = Math.abs(rawAmount);
      }
    } else {
      return;
    }

    transactions.push({
      date,
      description: description.trim(),
      amount: rawAmount,
      isCredit,
      rawAmount,
      rowIndex: idx
    });
  });
  return transactions;
}

// ---------------------------------------------------------------------------
// Date Range Extraction
// ---------------------------------------------------------------------------

function extractDateRange(transactions) {
  const times = transactions.map(tx => tx.date.getTime());
  return {
    dateFrom: new Date(Math.min(...times)),
    dateTo:   new Date(Math.max(...times))
  };
}

// ---------------------------------------------------------------------------
// Firestore — Last True-Up Date
// ---------------------------------------------------------------------------

async function getLastTrueUpDate() {
  try {
    const user = getCurrentUser();
    if (!user) return null;
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const ts = userDoc.data()?.lastTrueUpDate;
    return ts ? ts.toDate() : null;
  } catch (e) {
    console.warn('Could not read lastTrueUpDate:', e);
    return null;
  }
}

async function saveLastTrueUpDate() {
  try {
    const user = getCurrentUser();
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), {
      lastTrueUpDate: Timestamp.now()
    });
  } catch (e) {
    console.warn('Could not save lastTrueUpDate:', e);
  }
}

// ---------------------------------------------------------------------------
// Firestore — Load Logged Expenses
// ---------------------------------------------------------------------------

function getTargetUserId() {
  const user = getCurrentUser();
  if (!user) throw new Error('No user authenticated');
  const linkState = getLinkingState();
  return linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
}

async function loadLoggedExpenses(dateFrom, dateTo) {
  const targetUserId = getTargetUserId();
  const budgetId     = getCurrentBudgetId();
  if (!budgetId) return [];

  // Expand the query window by DATE_TOLERANCE on each side.
  // Banks post transactions 1-2 days after authorization; the user may have
  // logged the expense on the auth date, which falls before the earliest
  // posted bank date. Without this buffer the Firestore query filters those
  // expenses out before the matching algorithm ever sees them.
  const queryFrom = new Date(dateFrom.getTime() - DATE_TOLERANCE);
  const queryTo   = new Date(dateTo.getTime()   + DATE_TOLERANCE);
  queryTo.setHours(23, 59, 59, 999);

  const expensesQuery = query(
    collection(db, `users/${targetUserId}/expenses`),
    where('budgetId', '==', budgetId),
    where('date', '>=', Timestamp.fromDate(queryFrom)),
    where('date', '<=', Timestamp.fromDate(queryTo)),
    orderBy('date', 'desc'),
    limit(2000)
  );

  const snapshot = await getDocs(expensesQuery);
  return snapshot.docs.map(d => {
    const data = d.data();
    return {
      id:          d.id,
      amount:      parseFloat(data.amount) || 0,
      date:        data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
      description: data.description || '',
      isReturn:    !!data.isReturn
    };
  });
}

// ---------------------------------------------------------------------------
// Matching Algorithm
// ---------------------------------------------------------------------------

const AMOUNT_TOLERANCE = 0.01;        // $0.01
const DATE_TOLERANCE   = 172_800_000; // 2 days in ms

function matchTransactions(bankTxns, loggedExpenses) {
  // Clone so we can mark consumed
  const available = loggedExpenses.map(e => ({ ...e, _consumed: false }));

  const unmatched = [];
  const matched   = [];

  for (const tx of bankTxns) {
    if (tx.isCredit) {
      matched.push({ tx, expense: null, creditSkipped: true });
      continue;
    }

    // Find candidates
    let candidates = available.filter(e =>
      !e._consumed &&
      Math.abs(e.amount - tx.amount) <= AMOUNT_TOLERANCE &&
      Math.abs(e.date.getTime() - tx.date.getTime()) <= DATE_TOLERANCE
    );

    if (candidates.length > 0) {
      // Sort by date diff then amount diff, take best
      candidates.sort((a, b) => {
        const dateDiffA = Math.abs(a.date.getTime() - tx.date.getTime());
        const dateDiffB = Math.abs(b.date.getTime() - tx.date.getTime());
        if (dateDiffA !== dateDiffB) return dateDiffA - dateDiffB;
        return Math.abs(a.amount - tx.amount) - Math.abs(b.amount - tx.amount);
      });
      candidates[0]._consumed = true;
      matched.push({ tx, expense: candidates[0], creditSkipped: false });
    } else {
      unmatched.push(tx);
    }
  }

  return { unmatched, matched };
}

// ---------------------------------------------------------------------------
// Formatting Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount) {
  return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatDateShort(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateFull(date) {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatDateInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ---------------------------------------------------------------------------
// Step Renderers
// ---------------------------------------------------------------------------

async function renderStep1() {
  const body = document.getElementById('bank-true-up-body');
  if (!body) return;

  const lastTrueUp = await getLastTrueUpDate();
  const lastTrueUpText = lastTrueUp
    ? `Last check-in: ${formatDateFull(lastTrueUp)}`
    : 'No previous check-in recorded';

  body.innerHTML = `
    <div class="true-up-step1">
      <div class="true-up-last-date">${lastTrueUpText}</div>
      <p class="true-up-instructions">
        Upload your bank's CSV export to catch anything you haven't logged yet.
        Date range will be detected automatically from the file.
      </p>
      <div class="true-up-drop-zone" id="true-up-drop-zone">
        <div class="true-up-drop-icon">🏦</div>
        <div class="true-up-drop-text">Drop your bank CSV here, or click to browse</div>
        <div class="true-up-drop-subtext">Works with Chase, Bank of America, Wells Fargo, Capital One, Discover, Citi, USAA, and more</div>
        <input type="file" id="true-up-file-input" accept=".csv,text/csv" style="display:none">
      </div>
      <div class="true-up-file-name" id="true-up-file-name" style="display:none"></div>
      <div class="true-up-error" id="true-up-step1-error" style="display:none"></div>
    </div>
  `;

  // File input wiring
  const dropZone  = document.getElementById('true-up-drop-zone');
  const fileInput = document.getElementById('true-up-file-input');

  dropZone.addEventListener('click', () => fileInput.click());
  dropZone.addEventListener('dragover', e => { e.preventDefault(); dropZone.classList.add('drag-over'); });
  dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drag-over'));
  dropZone.addEventListener('drop', e => {
    e.preventDefault();
    dropZone.classList.remove('drag-over');
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  });
  fileInput.addEventListener('change', () => {
    if (fileInput.files[0]) handleFileSelected(fileInput.files[0]);
  });
}

function renderStep2() {
  const body = document.getElementById('bank-true-up-body');
  if (!body) return;

  const { headers, rows } = parseCSVText(trueUpState.rawCsvText);
  const sampleRows = rows.slice(0, 3);
  const headerOptions = headers.map((h, i) => `<option value="${i}">${h || `Column ${i + 1}`}</option>`).join('');
  const noneOption = `<option value="">-- Not present --</option>`;

  const previewRows = sampleRows.map(row =>
    `<tr>${row.map(cell => `<td title="${cell}">${cell.slice(0, 30)}</td>`).join('')}</tr>`
  ).join('');

  body.innerHTML = `
    <div class="true-up-step2">
      <p class="true-up-instructions">We couldn't auto-detect the columns. Please map them manually.</p>
      <div class="true-up-mapping-grid">
        <label>Date column</label>
        <select id="map-date-col">${headerOptions}</select>
        <label>Description column</label>
        <select id="map-desc-col">${headerOptions}</select>
        <label>Amount column</label>
        <select id="map-amount-col">${noneOption}${headerOptions}</select>
        <label>Debit column (if split)</label>
        <select id="map-debit-col">${noneOption}${headerOptions}</select>
        <label>Credit column (if split)</label>
        <select id="map-credit-col">${noneOption}${headerOptions}</select>
      </div>
      <div class="true-up-error" id="true-up-step2-error" style="display:none"></div>
      <div class="true-up-preview">
        <div class="true-up-preview-label">CSV Preview (first 3 rows)</div>
        <div class="true-up-preview-scroll">
          <table class="true-up-preview-table">
            <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
            <tbody>${previewRows}</tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

function renderStep3() {
  const body = document.getElementById('bank-true-up-body');
  if (!body) return;

  const { dateFrom, dateTo, unmatchedTransactions, matchedTransactions } = trueUpState;
  const totalTx    = trueUpState.parsedTransactions.length;
  const missedCount = unmatchedTransactions.length;
  const dayCount   = Math.round((dateTo - dateFrom) / 86_400_000) + 1;

  const escapeHtml = (str) => str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const renderTxRow = (tx, isMatched) => {
    const addedClass = trueUpState.addedFromTrueUp.has(tx.rowIndex) ? ' already-added' : '';
    if (isMatched && tx.creditSkipped) {
      const desc = escapeHtml(tx.tx.description);
      return `
        <div class="true-up-tx-row credit-row">
          <span class="true-up-tx-date">${formatDateShort(tx.tx.date)}</span>
          <span class="true-up-tx-desc" title="${desc}">${desc.slice(0, 60)}</span>
          <span class="true-up-tx-amount">${formatCurrency(tx.tx.amount)}</span>
          <span class="true-up-tx-action credit-label">Credit / Refund</span>
        </div>`;
    }
    if (isMatched) {
      const desc = escapeHtml(tx.tx.description);
      return `
        <div class="true-up-tx-row matched-row">
          <span class="true-up-tx-date">${formatDateShort(tx.tx.date)}</span>
          <span class="true-up-tx-desc" title="${desc}">${desc.slice(0, 60)}</span>
          <span class="true-up-tx-amount">${formatCurrency(tx.tx.amount)}</span>
          <span class="true-up-tx-action matched-label">✓ Logged</span>
        </div>`;
    }
    // Unmatched
    const desc = escapeHtml(tx.description);
    return `
      <div class="true-up-tx-row unmatched-row${addedClass}">
        <span class="true-up-tx-date">${formatDateShort(tx.date)}</span>
        <span class="true-up-tx-desc" title="${desc}">${desc.slice(0, 60)}</span>
        <span class="true-up-tx-amount">${formatCurrency(tx.amount)}</span>
        <span class="true-up-tx-action">
          ${trueUpState.addedFromTrueUp.has(tx.rowIndex)
            ? '<span class="added-label">Added ✓</span>'
            : `<button class="link-action-btn secondary true-up-add-btn" onclick="bankTrueUpAddTransaction(${tx.rowIndex})">Add</button>`
          }
        </span>
      </div>`;
  };

  const unmatchedHTML = unmatchedTransactions.length > 0
    ? unmatchedTransactions.map(tx => renderTxRow(tx, false)).join('')
    : '<div class="true-up-empty">🎉 All transactions are accounted for!</div>';

  const matchedHTML = matchedTransactions.length > 0
    ? matchedTransactions.map(tx => renderTxRow(tx, true)).join('')
    : '<div class="true-up-empty">No matched transactions found.</div>';

  body.innerHTML = `
    <div class="true-up-step3">
      <div class="true-up-summary-banner">
        Comparing ${formatDateShort(dateFrom)} – ${formatDateShort(dateTo)}
        &nbsp;•&nbsp; ${dayCount} days &nbsp;•&nbsp; ${totalTx} transactions
        &nbsp;•&nbsp; <strong>${missedCount} possibly missed</strong>
      </div>
      <div class="true-up-tabs">
        <button class="true-up-tab ${trueUpState.activeTab === 'unmatched' ? 'active' : ''}"
                onclick="showTrueUpTab('unmatched')">
          Possibly Missed <span class="true-up-badge">${unmatchedTransactions.length}</span>
        </button>
        <button class="true-up-tab ${trueUpState.activeTab === 'matched' ? 'active' : ''}"
                onclick="showTrueUpTab('matched')">
          Already Logged <span class="true-up-badge">${matchedTransactions.length}</span>
        </button>
      </div>
      <div class="true-up-tab-content" id="true-up-tab-unmatched"
           style="display:${trueUpState.activeTab === 'unmatched' ? 'block' : 'none'}">
        <div class="true-up-tx-header">
          <span>Date</span><span>Description</span><span>Amount</span><span>Action</span>
        </div>
        <div class="true-up-tx-list">${unmatchedHTML}</div>
      </div>
      <div class="true-up-tab-content" id="true-up-tab-matched"
           style="display:${trueUpState.activeTab === 'matched' ? 'block' : 'none'}">
        <div class="true-up-tx-header">
          <span>Date</span><span>Description</span><span>Amount</span><span>Status</span>
        </div>
        <div class="true-up-tx-list">${matchedHTML}</div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Step Indicator
// ---------------------------------------------------------------------------

function updateStepIndicator() {
  [1, 2, 3].forEach(n => {
    const el = document.getElementById(`true-up-step-${n}`);
    if (!el) return;
    el.classList.toggle('active',    n === trueUpState.step);
    el.classList.toggle('complete',  n < trueUpState.step);
  });
}

// ---------------------------------------------------------------------------
// Footer Button State
// ---------------------------------------------------------------------------

function updateFooterButtons() {
  const backBtn     = document.getElementById('true-up-back-btn');
  const continueBtn = document.getElementById('true-up-continue-btn');
  if (!backBtn || !continueBtn) return;

  backBtn.style.display     = trueUpState.step > 1 ? 'inline-flex' : 'none';
  continueBtn.textContent   = trueUpState.step === 3 ? 'Done' : 'Continue';
  continueBtn.style.display = 'inline-flex';
}

// ---------------------------------------------------------------------------
// File Handling
// ---------------------------------------------------------------------------

function handleFileSelected(file) {
  const errorEl   = document.getElementById('true-up-step1-error');
  const fileNameEl = document.getElementById('true-up-file-name');

  if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
    if (errorEl) { errorEl.textContent = 'Please select a CSV file.'; errorEl.style.display = 'block'; }
    return;
  }

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
  if (file.size > MAX_FILE_SIZE) {
    if (errorEl) { errorEl.textContent = 'File is too large. Please upload a CSV under 5 MB.'; errorEl.style.display = 'block'; }
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    trueUpState.rawCsvText = e.target.result;
    if (fileNameEl) {
      fileNameEl.textContent = `📎 ${file.name}`;
      fileNameEl.style.display = 'block';
    }
    if (errorEl) errorEl.style.display = 'none';
    // Auto-advance
    bankTrueUpNextStep();
  };
  reader.onerror = () => {
    if (errorEl) { errorEl.textContent = 'Error reading file.'; errorEl.style.display = 'block'; }
  };
  reader.readAsText(file);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initializeBankTrueUp() {
  // Move modal to document.body so position:fixed covers the full viewport,
  // not just the transformed/overflow ancestor (main-container).
  const modal = document.getElementById('bank-true-up-modal');
  if (modal && modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  // Auto-reopen the modal after a successful expense submission
  window.addEventListener('expense-submitted', () => {
    if (trueUpState.minimized) {
      resumeBankTrueUpModal();
    }
  });
  console.log('Bank True-Up module initialized');
}

export function openBankTrueUpModal() {
  // Reset state
  Object.assign(trueUpState, {
    step: 1,
    rawCsvText: null,
    parsedTransactions: [],
    columnMapping: null,
    dateFrom: null,
    dateTo: null,
    loggedExpenses: [],
    unmatchedTransactions: [],
    matchedTransactions: [],
    addedFromTrueUp: new Set(),
    activeTab: 'unmatched',
    minimized: false
  });
  hideTrueUpPill();

  const modal = document.getElementById('bank-true-up-modal');
  if (!modal) return;
  modal.classList.add('active');
  document.body.style.overflow = 'hidden';
  updateStepIndicator();
  updateFooterButtons();
  renderStep1();
}

export function closeBankTrueUpModal() {
  const modal = document.getElementById('bank-true-up-modal');
  if (!modal) return;
  trueUpState.minimized = false;
  modal.classList.remove('active');
  document.body.style.overflow = '';
  hideTrueUpPill();
}

function minimizeBankTrueUpModal() {
  const modal = document.getElementById('bank-true-up-modal');
  if (!modal) return;
  trueUpState.minimized = true;
  modal.classList.remove('active');
  modal.classList.add('minimized');
  document.body.style.overflow = '';
  updatePillText();
}

export function resumeBankTrueUpModal() {
  const modal = document.getElementById('bank-true-up-modal');
  if (!modal) return;
  trueUpState.minimized = false;
  modal.classList.remove('minimized');
  modal.classList.add('active');
  document.body.style.overflow = 'hidden';
  // Re-render step 3 so the updated "Added ✓" states are reflected
  renderStep3();
}

function updatePillText() {
  const pill = document.getElementById('bank-true-up-resume-pill');
  if (!pill) return;
  const remaining = trueUpState.unmatchedTransactions.filter(
    tx => !trueUpState.addedFromTrueUp.has(tx.rowIndex)
  ).length;
  const textEl = pill.querySelector('.pill-text');
  if (textEl) textEl.textContent =
    `Resume Check-In — ${remaining} item${remaining !== 1 ? 's' : ''} remaining`;
}

function hideTrueUpPill() {
  const modal = document.getElementById('bank-true-up-modal');
  if (modal) modal.classList.remove('minimized');
}

export async function bankTrueUpNextStep() {
  if (trueUpState.step === 1) {
    // Parse CSV
    if (!trueUpState.rawCsvText) {
      const errorEl = document.getElementById('true-up-step1-error');
      if (errorEl) { errorEl.textContent = 'Please select a CSV file first.'; errorEl.style.display = 'block'; }
      return;
    }

    const { headers, rows } = parseCSVText(trueUpState.rawCsvText);
    if (rows.length === 0) {
      const errorEl = document.getElementById('true-up-step1-error');
      if (errorEl) { errorEl.textContent = 'The CSV file appears to be empty.'; errorEl.style.display = 'block'; }
      return;
    }

    const sampleRows = rows.slice(0, 5);
    const mapping = detectColumnMapping(headers, sampleRows);

    if (!mapping) {
      // Go to step 2 for manual mapping
      trueUpState.step = 2;
      updateStepIndicator();
      updateFooterButtons();
      renderStep2();
      return;
    }

    trueUpState.columnMapping = mapping;
    trueUpState.parsedTransactions = buildTransactions(headers, rows, mapping);

    if (trueUpState.parsedTransactions.length === 0) {
      const errorEl = document.getElementById('true-up-step1-error');
      if (errorEl) { errorEl.textContent = 'No valid transactions found in this CSV.'; errorEl.style.display = 'block'; }
      return;
    }

    const { dateFrom, dateTo } = extractDateRange(trueUpState.parsedTransactions);
    trueUpState.dateFrom = dateFrom;
    trueUpState.dateTo   = dateTo;

    // Skip step 2 — go straight to loading + step 3
    await advanceToStep3();

  } else if (trueUpState.step === 2) {
    // Read manual mapping
    const dateColEl   = document.getElementById('map-date-col');
    const descColEl   = document.getElementById('map-desc-col');
    const amountColEl = document.getElementById('map-amount-col');
    const debitColEl  = document.getElementById('map-debit-col');
    const creditColEl = document.getElementById('map-credit-col');
    const errorEl     = document.getElementById('true-up-step2-error');

    const dateCol   = parseInt(dateColEl?.value);
    const descCol   = parseInt(descColEl?.value);
    const amountCol = amountColEl?.value !== '' ? parseInt(amountColEl.value) : null;
    const debitCol  = debitColEl?.value  !== '' ? parseInt(debitColEl.value)  : null;
    const creditCol = creditColEl?.value !== '' ? parseInt(creditColEl.value) : null;

    if (isNaN(dateCol) || isNaN(descCol)) {
      if (errorEl) { errorEl.textContent = 'Date and Description columns are required.'; errorEl.style.display = 'block'; }
      return;
    }
    if (amountCol === null && debitCol === null) {
      if (errorEl) { errorEl.textContent = 'Please select an Amount column or Debit/Credit columns.'; errorEl.style.display = 'block'; }
      return;
    }

    // Auto-detect format from CSV data
    const { headers, rows } = parseCSVText(trueUpState.rawCsvText);
    const sampleRows = rows.slice(0, 5);

    let dateFormat = 'MM/DD/YYYY';
    for (const row of sampleRows) {
      const val = row[dateCol];
      if (!val) continue;
      if (/^\d{4}-\d{2}-\d{2}/.test(val))   { dateFormat = 'YYYY-MM-DD'; break; }
      if (/^\d{2}\/\d{2}\/\d{4}/.test(val)) { dateFormat = 'MM/DD/YYYY'; break; }
      if (/^\d{2}-\d{2}-\d{4}/.test(val))   { dateFormat = 'MM-DD-YYYY'; break; }
    }

    let amountSign = 'negative-is-debit';
    if (amountCol !== null) {
      const samples = sampleRows
        .map(r => parseFloat(r[amountCol]?.replace(/[$,]/g, '') || ''))
        .filter(n => !isNaN(n));
      if (samples.length > 0 && samples.every(n => n >= 0)) amountSign = 'positive-is-debit';
    }

    trueUpState.columnMapping = {
      dateCol,
      descriptionCol: descCol,
      amountCol,
      debitCol,
      creditCol,
      dateFormat,
      amountSign: (debitCol !== null || creditCol !== null) ? 'split-columns' : amountSign
    };

    trueUpState.parsedTransactions = buildTransactions(headers, rows, trueUpState.columnMapping);

    if (trueUpState.parsedTransactions.length === 0) {
      if (errorEl) { errorEl.textContent = 'No valid transactions found with these column selections.'; errorEl.style.display = 'block'; }
      return;
    }

    const { dateFrom, dateTo } = extractDateRange(trueUpState.parsedTransactions);
    trueUpState.dateFrom = dateFrom;
    trueUpState.dateTo   = dateTo;

    await advanceToStep3();

  } else if (trueUpState.step === 3) {
    // Done
    closeBankTrueUpModal();
  }
}

async function advanceToStep3() {
  const continueBtn = document.getElementById('true-up-continue-btn');
  if (continueBtn) {
    continueBtn.disabled = true;
    continueBtn.textContent = 'Loading…';
  }

  try {
    trueUpState.loggedExpenses = await loadLoggedExpenses(trueUpState.dateFrom, trueUpState.dateTo);
    const { unmatched, matched } = matchTransactions(trueUpState.parsedTransactions, trueUpState.loggedExpenses);
    trueUpState.unmatchedTransactions = unmatched;
    trueUpState.matchedTransactions   = matched;

    // Save true-up date now that results are computed
    await saveLastTrueUpDate();

    trueUpState.step = 3;
    updateStepIndicator();
    updateFooterButtons();
    renderStep3();
  } catch (err) {
    console.error('Bank True-Up error:', err);
    const body = document.getElementById('bank-true-up-body');
    if (body) {
      body.innerHTML = `<div class="true-up-error-full">
        <p>⚠️ Error loading expenses: ${err.message}</p>
        <p style="font-size:var(--font-sm);margin-top:8px;">Please try again or check your connection.</p>
      </div>`;
    }
  } finally {
    if (continueBtn) {
      continueBtn.disabled = false;
      continueBtn.textContent = trueUpState.step === 3 ? 'Done' : 'Continue';
    }
  }
}

export function bankTrueUpPrevStep() {
  if (trueUpState.step <= 1) return;
  trueUpState.step--;
  updateStepIndicator();
  updateFooterButtons();
  if (trueUpState.step === 1) renderStep1();
  else if (trueUpState.step === 2) renderStep2();
}

export function bankTrueUpAddTransaction(rowIndex) {
  const tx = trueUpState.unmatchedTransactions.find(t => t.rowIndex === rowIndex);
  if (!tx) return;

  // Pre-fill the expense form
  const amountInput = document.getElementById('expense-amount-input');
  const descInput   = document.getElementById('expense-description-input');
  const dateInput   = document.getElementById('expense-date-input');
  const isReturnChk = document.getElementById('expense-is-return');

  if (amountInput) {
    amountInput.value = tx.amount.toFixed(2);
    amountInput.dispatchEvent(new Event('input', { bubbles: true }));
    amountInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  if (descInput) {
    descInput.value = tx.description.slice(0, 100);
    descInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
  if (dateInput) {
    dateInput.value = formatDateInput(tx.date);
    dateInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  if (isReturnChk) {
    isReturnChk.checked = tx.isCredit;
    isReturnChk.dispatchEvent(new Event('change', { bubbles: true }));
  }

  trueUpState.addedFromTrueUp.add(rowIndex);
  minimizeBankTrueUpModal();
}

export function showTrueUpTab(tab) {
  trueUpState.activeTab = tab;

  const unmatchedPanel = document.getElementById('true-up-tab-unmatched');
  const matchedPanel   = document.getElementById('true-up-tab-matched');
  const tabs           = document.querySelectorAll('.true-up-tab');

  if (unmatchedPanel) unmatchedPanel.style.display = tab === 'unmatched' ? 'block' : 'none';
  if (matchedPanel)   matchedPanel.style.display   = tab === 'matched'   ? 'block' : 'none';

  tabs.forEach(btn => {
    const isActive = (tab === 'unmatched' && btn.textContent.includes('Missed')) ||
                     (tab === 'matched'   && btn.textContent.includes('Logged'));
    btn.classList.toggle('active', isActive);
  });
}
